import { useMemo, useState } from "react";
import { AboutLockInfo } from '~/components/AboutLockInfo'
import { SISAmountInput } from '~/components/SISAmountInput'
import { SwitchNetworkButton } from '~/components/SwitchNetworkButton'
import Tile from '~/components/Tile'
import { useGetSISTokenBalance } from '~/hooks/useGetSISTokenBalance'
import { useToBigNumber } from '~/hooks/useToBigNumber'
import { useVESISParams } from '~/hooks/useVESISParams'

import { AboutLockInfoWithChange } from '../AboutLockInfoWithChange'
import type { Locked } from '../useLockChangeDetails'

import { useIncreaseAmount } from './useIncreaseAmount'

import styles from './IncreaseAmount.pcss'
import { useApproveSISToken } from '~/pages/ConfirmLockPage/useApproveSISToken'
import { SymbiosisButton } from '~/components/SymbiosisButton'
import { getApproveButtonText } from '~/pages/ConfirmLockPage/utils'
import { SimpleFraction } from "~/lib/SimpleFraction";
import { MAX_VESIS_WEEKS, SECONDS_IN_WEEK } from "~/lib/constants";
import { useLockNewDetails } from "~/pages/ConfirmLockPage/Details/useLockNewDetails";
import { BigNumber } from "ethers";

interface Props {
	locked?: Locked
}

export function IncreaseAmount({ locked }: Props) {
	const { config } = useVESISParams()
	const { data: balance } = useGetSISTokenBalance()

	const [amount, setAmount] = useState('')

	const amountBN = useToBigNumber(amount)
	const additionalAmount = amountBN || BigNumber.from(0)

	const duration = useMemo(() => {
		if (!locked) {
			return BigNumber.from(0)
		}
		const now = Math.floor(Date.now() / 1000)
		return new SimpleFraction(
			locked.end.sub(now),
			SECONDS_IN_WEEK
		).toBigNumber()
	}, [locked])

	const stats = useMemo(() => {
		if (!locked) {
			return
		}
		const additionalVESISAmount = duration
			.mul(additionalAmount)
			.div(MAX_VESIS_WEEKS)
		const newVESISAmount = additionalVESISAmount.add(locked.veSISBalance)

		const weeklyRewards = newVESISAmount
			.mul(locked.tokensPerWeek)
			.div(locked.totalVESISSupply)

		const totalRewards = weeklyRewards.mul(duration)

		return {
			lockAmount: locked.amount.add(additionalAmount),
			totalRewards,
			weeklyRewards,
		}
	}, [locked, duration, additionalAmount])

	const newLockDetails = useLockNewDetails(additionalAmount, duration.toNumber())

	const change = useMemo(() => {
		if (additionalAmount.isZero()) {
			return
		}
		return newLockDetails
	}, [additionalAmount, newLockDetails])

	const { needApprove, isApproving, approve } = useApproveSISToken(amountBN)
	const { mutate, isPending: isLoading } = useIncreaseAmount(amountBN)

	const amountEmpty = !amountBN?.gt(0)

	let insufficientBalance = false
	if (balance && amountBN) {
		insufficientBalance = amountBN.gt(balance)
	}

	let buttonText
	if (isLoading) {
		buttonText = 'Saving changes....'
	} else if (amountEmpty) {
		buttonText = 'Enter SIS Amount'
	} else if (insufficientBalance) {
		buttonText = 'Insufficient SIS balance'
	} else if (needApprove) {
		buttonText = 'You need approve SIS amount'
	} else {
		buttonText = 'Change Lock'
	}

	return (
		<>
			<div className={styles.container}>
				<SISAmountInput
					balance={balance}
					value={amount}
					disabled={isLoading}
					onChange={setAmount}
				/>

				<div className={styles.content}>
					<div className={styles.title}>LOCK CHANGES</div>
					<div className={styles.changes}>
						<AboutLockInfo
							label={
								amountEmpty
									? 'Locked amount'
									: 'New lock Amount:'
							}
							postfix="SIS"
							value={stats?.lockAmount}
							valueStyle={styles.lockAmount}
						/>
						<AboutLockInfoWithChange
							change={change?.weeklyRewards}
							label={`${
								amountEmpty ? '' : 'New'
							} Est. Weekly Rewards Value:`}
							postfix="SIS"
							value={stats?.weeklyRewards}
							invertPlaceholder
						/>
						<AboutLockInfoWithChange
							change={change?.totalRewards}
							label={`${
								amountEmpty ? '' : 'New'
							} Est. Total Rewards:`}
							postfix="SIS"
							value={stats?.totalRewards}
						/>
					</div>
					{amountBN && amountBN.gt(0) && needApprove && (
						<SymbiosisButton
							className={styles.approveButton}
							variant="white"
							disabled={!needApprove || isApproving}
							onClick={() => {
								approve()
							}}
						>
							{getApproveButtonText(isApproving, needApprove)}
						</SymbiosisButton>
					)}
				</div>
			</div>

			<Tile.Footer>
				<SwitchNetworkButton
					block
					color="black"
					disabled={
						isLoading ||
						needApprove ||
						amountEmpty ||
						insufficientBalance
					}
					onClick={() => {
						mutate(undefined, {
							onSuccess() {
								setAmount('')
							},
						})
					}}
					requiredChainId={config.chain.id}
				>
					{buttonText}
				</SwitchNetworkButton>
			</Tile.Footer>
		</>
	)
}
